import type { ManualQrisDecisionView, ManualQrisPaymentView } from "@kopi/contracts";
import { MANUAL_QRIS_STATUSES, type ManualQrisStatus } from "@kopi/contracts/codes";
import { zonedLocalToUtc } from "@kopi/domain";
import { Badge } from "@kopi/ui/components/badge";
import { Button } from "@kopi/ui/components/button";
import { DataTable, type DataTableColumn } from "@kopi/ui/components/data-table";
import { DatePicker } from "@kopi/ui/components/date-picker";
import { Input } from "@kopi/ui/components/input";
import { Label } from "@kopi/ui/components/label";
import { OptionSelect } from "@kopi/ui/components/option-select";
import { ResponsiveDialog } from "@kopi/ui/components/responsive-dialog";
import { toast } from "@kopi/ui/components/sonner";
import { Textarea } from "@kopi/ui/components/textarea";
import {
  infiniteQueryOptions,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { BadgeCheckIcon, BanIcon } from "lucide-react";
import { type FormEvent, useId, useMemo, useState } from "react";
import { useCurrentOutlet } from "@/components/current-outlet";
import { Page, PageHeader } from "@/components/page-header";
import { paymentVerifications } from "@/i18n/messages/payment-verifications";
import { useMessages } from "@/i18n/provider";
import { canAt } from "@/lib/access";
import { ApiRequestError, api, unwrap } from "@/lib/api";
import { meScopesQuery, outletsQuery, PAGE } from "@/lib/queries";
import { wallClockParts } from "@/lib/schedule";

const ALL = "all";
const KEY = ["manual-qris-payments"] as const;
const REFERENCE = /^[A-Z0-9][A-Z0-9._/-]{3,63}$/;

const useVerificationMessages = () => useMessages(paymentVerifications);
type VerificationsT = ReturnType<typeof useVerificationMessages>["t"];
type Outlet = { id: string; name: string; timezone: string };

/** The status filter lives in the URL; payments still to check are what a verifier opens it for. */
function validateSearch(search: Record<string, unknown>): { status: ManualQrisStatus | typeof ALL } {
  const status = search.status;
  return status === ALL || MANUAL_QRIS_STATUSES.includes(status as ManualQrisStatus)
    ? { status: status as ManualQrisStatus | typeof ALL }
    : { status: "awaiting_verification" };
}

/** An outlet's static-QRIS payments, newest first, a page at a time (CODING_STANDARDS §4). */
const paymentsQuery = (outletId: string, status: ManualQrisStatus | undefined) =>
  infiniteQueryOptions({
    queryKey: [...KEY, outletId, { status }],
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page: { nextCursor: string | null }) => page.nextCursor ?? undefined,
    queryFn: async ({ pageParam }) =>
      unwrap(
        await api.v1.outlets({ outletId })["manual-qris-payments"].get({
          query: { limit: PAGE, ...(pageParam ? { cursor: pageParam } : {}), ...(status ? { status } : {}) },
        }),
      ),
    enabled: Boolean(outletId),
  });

export const Route = createFileRoute("/_authed/payment-verifications")({
  staticData: { section: "workspace", crumb: "nav.paymentVerifications" },
  validateSearch,
  loader: ({ context }) => context.queryClient.ensureQueryData(outletsQuery),
  component: PaymentVerificationsPage,
});

function StatusBadge(props: { payment: ManualQrisPaymentView }) {
  const { t } = useVerificationMessages();
  const { payment } = props;
  if (payment.reviewCaseId) return <Badge variant="warning">{t("paymentVerifications.status.review")}</Badge>;
  const variant =
    payment.status === "captured" ? "success" : payment.status === "rejected" ? "destructive" : "warning";
  return <Badge variant={variant}>{t(`paymentVerifications.status.${payment.status}`)}</Badge>;
}

const columnsFor = (t: VerificationsT, money: (v: bigint) => string, when: (iso: string) => string) =>
  [
    {
      id: "order",
      header: t("paymentVerifications.order"),
      meta: { card: "title" },
      cell: ({ row }) => (
        <div className="flex min-w-0 flex-col">
          <span className="font-medium font-mono">
            {t("paymentVerifications.reference", { reference: row.original.reference })}
          </span>
          {row.original.receiptNumber && (
            <span className="text-muted-foreground text-sm tabular-nums">
              {t("paymentVerifications.receipt", { number: row.original.receiptNumber })}
            </span>
          )}
        </div>
      ),
    },
    {
      id: "recorded",
      header: t("paymentVerifications.recorded"),
      cell: ({ row }) => (
        <div className="flex min-w-0 flex-col">
          <time dateTime={row.original.recordedAt}>{when(row.original.recordedAt)}</time>
          <span className="text-muted-foreground text-sm">
            {t("paymentVerifications.recordedBy", {
              name: row.original.recordedBy,
              register: row.original.registerName,
            })}
          </span>
        </div>
      ),
    },
    {
      id: "status",
      header: t("paymentVerifications.status"),
      meta: { className: "md:w-36" },
      cell: ({ row }) => <StatusBadge payment={row.original} />,
    },
    {
      id: "decision",
      header: t("paymentVerifications.decision"),
      cell: ({ row }) => {
        const v = row.original.verification;
        if (row.original.reviewCaseId)
          return (
            <span className="text-muted-foreground text-sm">{t("paymentVerifications.reviewHint")}</span>
          );
        if (!v || v.outcome === "duplicate") return <span className="text-muted-foreground">—</span>;
        return (
          <span
            className="block max-w-56 truncate text-sm md:max-w-72"
            title={v.merchantReference ?? v.note ?? ""}
          >
            {v.outcome === "verified"
              ? t("paymentVerifications.verifiedBy", {
                  reference: v.merchantReference ?? "",
                  name: v.verifiedBy,
                  time: when(v.verifiedAt),
                })
              : t("paymentVerifications.rejectedBy", {
                  name: v.verifiedBy,
                  time: when(v.verifiedAt),
                  note: v.note ?? "",
                })}
          </span>
        );
      },
    },
    {
      id: "amount",
      header: t("paymentVerifications.amount"),
      meta: { align: "end" },
      cell: ({ row }) => <span className="tabular-nums">{money(BigInt(row.original.amount))}</span>,
    },
  ] satisfies DataTableColumn<ManualQrisPaymentView>[];

/**
 * Static-QRIS verification (R1-28, PRD PAY-03, AC-36): payments the register took to the merchant's
 * printed QRIS while the gateway was out. Each is BELUM LUNAS until someone with `payments.verify`
 * finds it in the merchant account and records its reference, amount and time — or rejects it.
 */
function PaymentVerificationsPage() {
  const { t, format } = useVerificationMessages();
  const { data: scopes } = useSuspenseQuery(meScopesQuery);
  const { data: outlets } = useSuspenseQuery(outletsQuery);
  const { outlet: current } = useCurrentOutlet();
  const verifiable = outlets.filter((o) => canAt(scopes, "payments.verify", o.id));
  const [outletId, setOutletId] = useState(
    verifiable.find((o) => o.id === current?.id)?.id ?? verifiable[0]?.id ?? "",
  );
  const outlet = verifiable.find((o) => o.id === outletId);
  const { status } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const list = useInfiniteQuery(paymentsQuery(outletId, status === ALL ? undefined : status));
  const [deciding, setDeciding] = useState<{
    payment: ManualQrisPaymentView;
    kind: "verify" | "reject";
  } | null>(null);
  const columns = useMemo(
    () =>
      columnsFor(
        t,
        (v) => format.money(v),
        (iso) => format.dateTime(iso, outlet?.timezone),
      ),
    [t, format, outlet?.timezone],
  );

  if (verifiable.length === 0 || !outlet) {
    return (
      <Page>
        <PageHeader title={t("nav.paymentVerifications")} subtitle={t("paymentVerifications.noOutlet")} />
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader title={t("nav.paymentVerifications")} subtitle={t("paymentVerifications.subtitle")} />
      <DataTable
        paging="pages"
        label={t("paymentVerifications.list")}
        columns={columns}
        query={list}
        getRowId={(p) => p.paymentAttemptId}
        filters={
          <>
            {/* A tenant's outlets are a small, bounded set (CODING_STANDARDS §5: OptionSelect is fine). */}
            <OptionSelect
              aria-label={t("outlet.heading")}
              className="md:w-56"
              options={verifiable.map((o) => ({ value: o.id, label: o.name }))}
              value={outletId}
              onValueChange={setOutletId}
            />
            <OptionSelect
              aria-label={t("paymentVerifications.status")}
              className="md:w-56"
              options={[
                { value: ALL, label: t("paymentVerifications.status.all") },
                ...MANUAL_QRIS_STATUSES.map((s) => ({
                  value: s,
                  label: t(`paymentVerifications.status.${s}`),
                })),
              ]}
              value={status}
              onValueChange={(value) =>
                void navigate({ search: { status: value as ManualQrisStatus | typeof ALL }, replace: true })
              }
            />
          </>
        }
        rowActions={(p) =>
          p.status === "awaiting_verification" && !p.reviewCaseId
            ? [
                {
                  label: t("paymentVerifications.verify"),
                  icon: <BadgeCheckIcon />,
                  onSelect: () => setDeciding({ payment: p, kind: "verify" }),
                },
                {
                  label: t("paymentVerifications.reject"),
                  icon: <BanIcon />,
                  onSelect: () => setDeciding({ payment: p, kind: "reject" }),
                },
              ]
            : []
        }
        empty={{
          title: t("paymentVerifications.emptyTitle"),
          description: t("paymentVerifications.emptyHint"),
        }}
      />
      {deciding?.kind === "verify" && (
        <VerifyDialog payment={deciding.payment} outlet={outlet} onClose={() => setDeciding(null)} />
      )}
      {deciding?.kind === "reject" && (
        <RejectDialog payment={deciding.payment} onClose={() => setDeciding(null)} />
      )}
    </Page>
  );
}

/** Why a decision was refused, in the interface language (the API's English message is never shown). */
function decisionError(t: VerificationsT, error: unknown): string {
  const code = error instanceof ApiRequestError ? error.code : null;
  if (code === "amount_mismatch") return t("paymentVerifications.error.amount");
  if (code === "paid_at_out_of_range") return t("paymentVerifications.error.paidAt");
  if (code === "already_decided" || code === "under_review") return t("paymentVerifications.error.changed");
  return t("paymentVerifications.error.failed");
}

/** Records what the merchant account shows: its reference, the amount received and when it was paid. */
function VerifyDialog(props: { payment: ManualQrisPaymentView; outlet: Outlet; onClose: () => void }) {
  const { payment, outlet } = props;
  const { t, format, locale } = useVerificationMessages();
  const queryClient = useQueryClient();
  const ids = {
    reference: useId(),
    amount: useId(),
    day: useId(),
    dayLabel: useId(),
    time: useId(),
    note: useId(),
  };
  const recorded = wallClockParts(new Date(payment.recordedAt), outlet.timezone);
  const [reference, setReference] = useState("");
  const [amount, setAmount] = useState("");
  const [day, setDay] = useState(recorded.day);
  const [time, setTime] = useState(recorded.time);
  const [note, setNote] = useState("");
  const cleanReference = reference.replace(/\s+/g, "").toUpperCase();
  const valid = REFERENCE.test(cleanReference) && amount !== "" && day !== "" && time !== "";
  const save = useMutation({
    mutationFn: async (): Promise<ManualQrisDecisionView> =>
      unwrap(
        await api.v1["manual-qris-payments"]({ attemptId: payment.paymentAttemptId }).verify.post({
          merchantReference: cleanReference,
          amount,
          paidAt: zonedLocalToUtc(`${day}T${time}`, outlet.timezone).toISOString(),
          ...(note.trim() ? { note: note.trim() } : {}),
        }),
      ),
    onSuccess: async (decided) => {
      await queryClient.invalidateQueries({ queryKey: KEY });
      await queryClient.invalidateQueries({ queryKey: ["payment-exceptions"] });
      if (decided.outcome === "verified")
        toast.success(t("paymentVerifications.verified", { number: decided.payment.receiptNumber ?? "" }));
      else toast.warning(t("paymentVerifications.duplicate"));
      props.onClose();
    },
  });
  function submit(event: FormEvent) {
    event.preventDefault();
    if (valid && !save.isPending) save.mutate();
  }
  return (
    <ResponsiveDialog
      open
      onOpenChange={(open) => !open && props.onClose()}
      title={t("paymentVerifications.verifyTitle")}
      description={`${t("paymentVerifications.reference", { reference: payment.reference })} · ${format.money(BigInt(payment.amount))}`}
      dismissible={!save.isPending}
      footer={
        <Button type="submit" form="verify-manual-qris" disabled={!valid || save.isPending}>
          {t(save.isPending ? "paymentVerifications.saving" : "paymentVerifications.verifyAction")}
        </Button>
      }
    >
      <form id="verify-manual-qris" className="flex flex-col gap-4" onSubmit={submit} noValidate>
        <p className="text-muted-foreground text-sm">{t("paymentVerifications.verifyHint")}</p>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={ids.reference}>{t("paymentVerifications.merchantReference")}</Label>
          <Input
            id={ids.reference}
            maxLength={80}
            autoComplete="off"
            spellCheck={false}
            className="font-mono"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
          />
          <p className="text-muted-foreground text-sm">{t("paymentVerifications.merchantReferenceHint")}</p>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={ids.amount}>{t("paymentVerifications.received")}</Label>
          <Input
            id={ids.amount}
            inputMode="numeric"
            autoComplete="off"
            className="tabular-nums"
            value={amount}
            onChange={(e) =>
              setAmount(
                e.target.value
                  .replace(/\D/g, "")
                  .replace(/^0+(?=\d)/, "")
                  .slice(0, 12),
              )
            }
          />
          <p className="text-muted-foreground text-sm tabular-nums">
            {t("paymentVerifications.receivedHint", {
              amount: format.money(BigInt(payment.amount)),
              preview: format.money(BigInt(amount || "0")),
            })}
          </p>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label id={ids.dayLabel} htmlFor={ids.day}>
            {t("paymentVerifications.paidAt", { timezone: outlet.timezone })}
          </Label>
          <div className="grid grid-cols-[1fr_8rem] gap-2">
            <DatePicker locale={locale} id={ids.day} labelId={ids.dayLabel} value={day} onChange={setDay} />
            <Label htmlFor={ids.time} className="sr-only">
              {t("paymentVerifications.paidTime")}
            </Label>
            <Input id={ids.time} type="time" value={time} onChange={(e) => setTime(e.target.value)} />
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={ids.note}>{t("paymentVerifications.evidence")}</Label>
          <Textarea
            id={ids.note}
            maxLength={500}
            value={note}
            placeholder={t("paymentVerifications.evidencePlaceholder")}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
        {save.isError && (
          <p role="alert" className="text-destructive text-sm">
            {decisionError(t, save.error)}
          </p>
        )}
      </form>
    </ResponsiveDialog>
  );
}

/** The payment isn't in the merchant account: rejected, with the reason; the order stays unpaid. */
function RejectDialog(props: { payment: ManualQrisPaymentView; onClose: () => void }) {
  const { payment } = props;
  const { t, format } = useVerificationMessages();
  const queryClient = useQueryClient();
  const noteId = useId();
  const [note, setNote] = useState("");
  const valid = note.trim().length >= 3;
  const save = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.v1["manual-qris-payments"]({ attemptId: payment.paymentAttemptId }).reject.post({
          note: note.trim(),
        }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: KEY });
      toast.success(t("paymentVerifications.rejected"));
      props.onClose();
    },
  });
  function submit(event: FormEvent) {
    event.preventDefault();
    if (valid && !save.isPending) save.mutate();
  }
  return (
    <ResponsiveDialog
      open
      onOpenChange={(open) => !open && props.onClose()}
      title={t("paymentVerifications.rejectTitle")}
      description={`${t("paymentVerifications.reference", { reference: payment.reference })} · ${format.money(BigInt(payment.amount))}`}
      dismissible={!save.isPending}
      footer={
        <Button
          type="submit"
          variant="destructive"
          form="reject-manual-qris"
          disabled={!valid || save.isPending}
        >
          {t(save.isPending ? "paymentVerifications.saving" : "paymentVerifications.rejectAction")}
        </Button>
      }
    >
      <form id="reject-manual-qris" className="flex flex-col gap-4" onSubmit={submit} noValidate>
        <p className="text-muted-foreground text-sm">{t("paymentVerifications.rejectHint")}</p>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={noteId}>{t("paymentVerifications.rejectReason")}</Label>
          <Textarea
            id={noteId}
            maxLength={500}
            value={note}
            placeholder={t("paymentVerifications.rejectPlaceholder")}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
        {save.isError && (
          <p role="alert" className="text-destructive text-sm">
            {decisionError(t, save.error)}
          </p>
        )}
      </form>
    </ResponsiveDialog>
  );
}
